<script setup>
import { onBeforeMount, onMounted, onUnmounted } from 'vue'
import { useMainStore } from '@/stores/main'
import Slideover from '@/components/SlideOver/slideover.vue'
import modal from '@/components/modals/modal.vue'
import MOTDToast from '@/components/MOTDToast.vue'
import ScratchpadEditor from '@/components/SlideOver/forms/ScratchpadEditor.vue'
import GuideOverlay from '@/guides/GuideOverlay.vue'
import Sortable, { MultiDrag, Swap } from 'sortablejs';

let mainStore = useMainStore()

onBeforeMount( async () => {
  window.Sortable = Sortable;
  await mainStore.init()
})

// Global keyboard shortcuts
function handleGlobalKeydown(event) {
  // ESC key - Pop topmost slideover (issue #163)
  if (event.key === 'Escape' && mainStore.slideover_show) {
    event.preventDefault()
    mainStore.pop_slideover()
    return
  }

  // Ctrl+. / Cmd+. - Toggle scratchpad
  if ((event.ctrlKey || event.metaKey) && event.key === '.') {
    const topSlideover = mainStore.slideover_history[mainStore.slideover_history.length - 1]
    const scratchpadOpen = mainStore.slideover_show && topSlideover?.name === 'Scratchpad'

    // Always allow closing the scratchpad, but block opening when in input/textarea/contenteditable
    if (!scratchpadOpen) {
      const activeEl = document.activeElement
      const tagName = activeEl?.tagName?.toLowerCase()
      if (tagName === 'input' || tagName === 'textarea' || activeEl?.isContentEditable) {
        return
      }
    }

    event.preventDefault()

    // Toggle scratchpad
    if (scratchpadOpen) {
      mainStore.close_slideover()
    } else {
      mainStore.show_slideover(ScratchpadEditor, {}, { width: 50 }, 'Scratchpad')
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})
</script>

<template>
  <div style="height: 100%;" ondragstart="return false;">
    <slideover />
    <modal :show="mainStore.modal_show" :component="mainStore.modal_component" :properties="mainStore.modal_props" @modal-close="mainStore.close_modal()" :fullscreen="mainStore.modal_fullscreen"></modal>
    <MOTDToast />
    <GuideOverlay />
    <router-view/>
  </div>
</template>

<style>
/* Design tokens — single source of truth for Webscout's visual language.
   See ./styles/tokens.css and ./DESIGN_SYSTEM.md. */
@import './styles/tokens.css';

:root {
  --free-plan: rgb(246 211 129);
  --basic-plan: rgb(246 129 237);
  --pro-plan: rgb(141 129 246);
  --elite-plan: rgb(129 186 246);
  --enterprise-plan: #FFFFFF;
  --danger: var(--color-error); 
  --warning: var(--color-warning);
}

#app {
  /*font-family: Sansation, Montserrat, Arial, sans-serif;*/

  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  scroll-behavior: smooth; 
}
@font-face {
  font-family: Montserrat;
  src: url('~@/assets/fonts/Montserrat-VariableFont_wght.ttf');
}

@font-face {
  font-family: Montserrat;
  src: url('~@/assets/fonts/Montserrat-Black.ttf');
  font-weight: 900;
}

@font-face {
  font-family: Sansation;
  src: url('~@/assets/fonts/Sansation_Regular.ttf');
  font-weight: 400;
}

@font-face {
  font-family: Sansation;
  src: url('~@/assets/fonts/Sansation_Bold.ttf');
  font-weight: 600;
}

@font-face {
  font-family: Sansation;
  src: url('~@/assets/fonts/Sansation_Light.ttf');
  font-weight: 300;
}

@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap');

@import url('https://fonts.googleapis.com/css2?family=Cutive+Mono&display=swap');


html, body, #__nuxt {
  min-height: 100% !important;
  height: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: var(--surface-page);
  font-family: "Inter", sans-serif;
  color: var(--text-secondary);
}

h1,h2,h3,h4,h5,h6 {
  font-family: Roboto;
  font-weight: 900;
  color:white;
}

h3,h4,h5,h6 {
  font-family: Roboto;
  font-weight: 500;
  color:white;
}

.jura {
  font-family: "Jura", sans-serif;
  font-style: normal;
}

.wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
}

.wrapper.user {
    background-color: var(--surface-card);
}

.wrapper-content {
    flex: 1;
    min-height: 0;
    /* min-height: 100%; */
    display: flex;
    overflow: auto;
    overflow-x: clip;
    scroll-behavior: smooth;
    /* overflow-y: scroll; */
}

.wrapper.user .wrapper-content {
  background-color: var(--surface-page);
  border-radius: var(--radius-xl) var(--radius-xl) var(--radius-xl) 0;
  overflow: hidden;
}

.mbb-4 {
    margin-bottom: 4rem;
}

.mtt-5 {
    margin-top: 5rem;
}

.mtt-8 {
    margin-top: 8rem;
}

.mbb-5 {
    margin-bottom: 5rem;
}


.content {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    /* padding-bottom: 1rem; */
    /* overflow: scroll; */
}

.bold {
  font-weight: bold;
}

.fill {
  flex: 1; 
  display: flex;
}

/* General form styling */

.form-check {
    display: flex;
}

.popup {
  position: absolute;
  bottom: 1rem;
  background: black;
  color: white;
  padding: 1rem;
  border-radius: var(--radius-md);
  right: 1rem;
}


.form-control, .form-select, .form-check-input[type="checkbox"] {
  background-color: var(--surface-input);
  border:none;
  outline:none;
  color:white;
  /*font-weight: bold;*/
  border-radius: var(--radius-md);
}

 .form-check-input[type="checkbox"] {
   padding:.7rem;
   border-radius: var(--radius-sm);
   margin-right: 0.7rem;
 }

.form-control:active, .form-control:focus, .form-check-input[type="checkbox"] {
  background-color: var(--surface-input);
  outline: none;
  box-shadow: none;
  color:white;
}

 .form-control.dark, .form-control.dark:active, .form-check-input.dark, .form-control.dark:focus {
  background-color: var(--surface-inset);
 }

.form-floating > label {
    /*font-weight: bold;*/
    color: var(--text-secondary);
    padding: 1.3rem 2rem;
}

.form-select > label {
    font-weight: bold;
    color: var(--text-secondary);
    padding: 1.3rem 2rem;
}

.form-select:focus-visible {
  color: white;
  /* text-shadow: 0 0 0 #212529; */
  text-shadow: none;
}

.form-floating > .form-control {
  padding: 2rem 2rem;
  padding-left: 1.8rem;
}

.wrap {
  flex-wrap: wrap;
}

.form-floating > .form-select {
  padding-top: 1.2rem;
  padding-left: 1.6rem;
  padding-bottom: 0rem;
  height: 64px;
}

.form-floating > .form-control:focus, .form-floating > .form-control:not(:placeholder-shown) {
  padding-top: 2.5rem;
  padding-bottom: 1.5rem;
}

.form-check-label {
  line-height: 2rem;
  color: white;
}

.ws-gradient {
  background: var(--brand-gradient);
}

.ws-gradient-text {
  background: var(--brand-gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.roboto-thin {
  font-family: "Roboto", sans-serif;
  font-weight: 100;
  font-style: normal;
}

.roboto-light {
  font-family: "Roboto", sans-serif;
  font-weight: 300;
  font-style: normal;
}

.roboto-regular {
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-style: normal;
}

.roboto-medium {
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-style: normal;
}

.roboto-bold {
  font-family: "Roboto", sans-serif;
  font-weight: 700;
  font-style: normal;
}

.roboto-black {
  font-family: "Roboto", sans-serif;
  font-weight: 900;
  font-style: normal;
}

.roboto-thin-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 100;
  font-style: italic;
}

.roboto-light-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 300;
  font-style: italic;
}

.roboto-regular-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-style: italic;
}

.roboto-medium-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-style: italic;
}

.roboto-bold-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 700;
  font-style: italic;
}

.roboto-black-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 900;
  font-style: italic;
}

a {
  text-decoration: none;
}

.alert {
  border-radius: var(--radius-md);
}

.box {
    background-color: var(--surface-raised);
    border-radius: var(--radius-md);
    position: relative;
    overflow: hidden;
}


.box.seethrough {
    background-color: #1E252FCF;
  }

.box.padded {
    padding: 1.5rem 2rem;
}

.box.dark {
  background-color: var(--surface-inset);

}

.box.bordered {
  border: 1px solid var(--border);
}

.box.dark.bordered {
  border: 1px solid var(--border);
}


.box.small-padded {
  padding: .4rem 1.1rem;
}

.box.med-padded {
    padding: .7rem 1.5rem;
}

.box.vert-center {
  align-items: center;
}


.box.hori-center {
  justify-content: center;
}

.box.success {
  background-color: var(--color-success);
}

.white {
  color: white;
}

.box.squared {
  border-radius: var(--radius-md);
}

.box.bg-d {
  background-color: var(--surface-page);
}

.box.bg-l {
  background-color: var(--surface-raised);
}

.box .header {
    padding: 1.5rem 2rem;
}

.box .element-header {
    display: flex;
    
  }

.box .element-header .main {
    color: white;
    background-color: var(--surface-raised);
    padding: .8rem 3rem .8rem 1.5rem;
    border-radius: 0 0 var(--radius-xl) 0;
    font-weight: bold;
    font-size: 1.1rem;
    z-index: 1;
  }

.element-header .sub {
    background-color: var(--surface-raised);
    padding: .8rem 3rem .8rem 3rem;
    border-radius: 0 0 var(--radius-xl) 0;
    left: -10px;
    position: relative;
  }

.box.clickable {
  cursor: pointer;
}

.box.clickable:hover {
    background-color: var(--surface-raised);
}

.box.success:hover {
  background-color: var(--color-success);
}

.box.faded {
  opacity: .4;
}

.box.faded.clickable {
  cursor:pointer;
  transition: opacity ease .35s;
}

.box.faded.clickable:hover {
  opacity: .7;
}


.modal-header {
  padding: 1rem 1.5rem; 
  border-bottom: none;
}

.modal-footer {
  border-top: none;
}

.modal-content {
  background-color: var(--surface-page);
  border-radius: var(--radius-xl);
}

.modal-body {
    padding: 3rem;
}


.bg-360 {
  background-color: var(--color-info);
}

.bg-public {
  background-color: var(--accent-text);
}

.bg-scan {
  background-color: var(--accent);
}

.dropdown-item {
    display: flex;
    align-items: center;
    padding: .5rem 2rem .5rem 1rem;
  border-radius: var(--radius-md);
}

.dropdown-item:hover {
    background-color: #eff3f7;
    /* background-color: transparent; */
}

.subheader {
    font-weight: 500;
    color: var(--text-faint);
    font-family: Sansation;
}

.box.transparent {
    background-color: transparent;
  }


.danger-bg {
  background-color: var(--color-error);
  stroke: var(--color-error);
  color: white;
}

.danger-bg-darker {
  background-color: var(--color-error);
  stroke: var(--color-error);
}

.warning-bg {
  background-color: var(--color-warning);
  stroke: var(--color-warning);
  color: white;
}

.notice-bg {
  background-color: var(--color-info-alt);
  color: white;
}

.good-bg {
  background-color: var(--color-success);
  color:white;
}

.btn-continue {
    padding: .6rem 2rem;
    background-color:var(--accent);
    font-weight: bold;
    outline:none;
    box-shadow: none;
    border:none;
    color: white;
    border-radius: var(--radius-xl);
}

.btn-continue:hover {
    background-color: var(--accent);
}

.btn-pink {
    padding: .6rem 2rem;
    background-color:var(--accent-text);
    font-weight: bold;
    outline:none;
    box-shadow: none;
    border:none;
    color: white;
    border-radius: var(--radius-xl);
}

.btn-pink:hover {
    background-color: var(--accent-text);
    color:white;
}

.btn-blue {
    padding: .6rem 2rem;
    background-color:var(--color-info);
    font-weight: bold;
    outline:none;
    box-shadow: none;
    border:none;
    color: white;
    border-radius: var(--radius-xl);
}

.btn-blue:hover {
    background-color: var(--color-info);
    color:white;
}

.alert-box {
  line-height: 1rem;
  background-color: var(--color-error);
  border-radius: var(--radius-md);
  font-weight: bold;
  color: white;
  padding: .2rem 0.6rem;
  display: flex;
  align-items: center;
  font-size: .9rem;
}

.centered {
  justify-content: center;
}

.column {
  flex-direction: column;
}
.box.drow {
  flex-direction: row;
}

.drow.centered {
  align-items: center
}


.column.centered {
  align-items: center;
}

.vertically-centered {
  align-items: center;
}

.column.vertically-centered {
  justify-content: center;
}


.warning-box {
  line-height: 1rem;
  background-color: var(--color-warning);
  border-radius: var(--radius-md);
  font-weight: bold;
  color: white;
  padding: .2rem 0.6rem;
  display: flex;
  align-items: center;
  font-size: .9rem;
}

.info-box {
  line-height: 1rem;
  background-color: var(--accent);
  border-radius: var(--radius-md);
  font-weight: bold;
  color: white;
  padding: .2rem 0.6rem;
  display: flex;
  align-items: center;
  font-size: .9rem;
}

.grayed {
    color: var(--text-primary);
}

/* DataTable styling */
.datatable tr {
  border-color: var(--border);
  cursor: pointer;
}

.datatable td {
  color: white !important;
}

::-webkit-scrollbar {
  width: 20px;
  height: 20px;
  scroll-behavior: smooth;
}

::-webkit-scrollbar-track {
  background-color: transparent;
}

::-webkit-scrollbar-thumb {
  background-color: var(--surface-control);
  border-radius: 20px;
  border: 6px solid transparent;
  background-clip: content-box;
}

::-webkit-scrollbar-thumb:hover {
  background-color: #a8bbbf;
}

::-webkit-scrollbar-button { 
   display:none;
}

::-webkit-scrollbar-corner {
  background: transparent;
}

.hide-desktop {
  display: none;
}

@media (max-width: 600px) {
  .hide-mobile {
    display: none;
  }
  .hide-desktop {
    display: initial;
  }
}

.form-select:disabled {
  background-color: var(--surface-input);
  color: #ffffffba;
}

.uppercase {
  text-transform: uppercase;
}

/* Toast Notifications - Custom Dark Theme */
:deep(.Vue-Toastification__toast) {
  background-color: var(--surface-raised) !important;
  border: 1px solid var(--border-subtle) !important;
  border-radius: var(--radius-lg) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5) !important;
  padding: 1rem 1.25rem !important;
  font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
}

:deep(.Vue-Toastification__toast--success) {
  border-left: 4px solid var(--color-success) !important;
}

:deep(.Vue-Toastification__toast--error) {
  border-left: 4px solid var(--color-error) !important;
}

:deep(.Vue-Toastification__toast--warning) {
  border-left: 4px solid var(--color-warning) !important;
}

:deep(.Vue-Toastification__toast--info) {
  border-left: 4px solid var(--color-info) !important;
}

:deep(.Vue-Toastification__toast-body) {
  color: var(--text-primary) !important;
  font-size: 0.9rem !important;
  line-height: 1.5 !important;
}

:deep(.Vue-Toastification__progress-bar) {
  background: linear-gradient(90deg, rgba(168, 85, 247, 0.5), rgba(168, 85, 247, 0.8)) !important;
}

:deep(.Vue-Toastification__close-button) {
  color: var(--text-muted) !important;
  opacity: 0.7 !important;
}

:deep(.Vue-Toastification__close-button:hover) {
  opacity: 1 !important;
  color: var(--text-primary) !important;
}

:deep(.Vue-Toastification__icon) {
  width: 20px !important;
  margin-right: 0.75rem !important;
}
</style>
